import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Loader2, ShieldCheck, Trash2, Pencil } from "lucide-react";
import { PasswordStrength, isStrongPassword } from "@/components/PasswordStrength";
import { toast } from "sonner";

export default function SuperAdmins() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "", password: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setAdmins(data.filter((u) => u.role === "super_admin"));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!isStrongPassword(form.password)) return toast.error("Password must be at least 8 characters and include both letters and numbers");
    setBusy(true);
    try {
      await api.post("/users", { ...form, role: "super_admin" });
      toast.success("Super administrator created");
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (u) => {
    try { await api.delete(`/users/${u.user_id}`); toast.success("Super administrator deleted"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name || "", email: u.email || "", password: "" });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (editForm.password && !isStrongPassword(editForm.password)) return toast.error("Password must be at least 8 characters and include both letters and numbers");
    setBusy(true);
    try {
      const payload = { name: editForm.name, email: editForm.email };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${editing.user_id}`, payload);
      toast.success("Super administrator updated");
      setEditOpen(false);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="super-admins-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          Global administrators with access to every tenant. Create or remove other super administrators here.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="new-super-admin-btn"><Plus className="h-4 w-4 mr-2" />New Super Admin</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Super Administrator</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="space-y-2"><Label>Name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="super-admin-name" className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Email</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="super-admin-email" className="rounded-sm" /></div>
              <div className="space-y-2"><Label>Password <span className="text-muted-foreground text-xs">(min 8, letters &amp; numbers)</span></Label>
                <PasswordInput required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="super-admin-password" className="rounded-sm" />
                <PasswordStrength value={form.password} /></div>
              <DialogFooter>
                <Button type="submit" disabled={busy} className="rounded-sm" data-testid="super-admin-submit">
                  {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : admins.length === 0 ? (
          <div className="p-12 text-center">
            <ShieldCheck className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No super administrators.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.map((u) => (
                <TableRow key={u.user_id} className="hover:bg-muted/50" data-testid={`super-admin-row-${u.user_id}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="font-mono text-xs">{u.email}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`super-admin-edit-${u.user_id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {u.user_id === user.user_id ? (
                        <span className="text-xs text-muted-foreground pl-1">You</span>
                      ) : (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`super-admin-delete-${u.user_id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent data-testid="delete-super-admin-dialog">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This permanently removes super administrator <span className="font-medium">{u.email}</span>. They will lose global access immediately. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`super-admin-delete-cancel-${u.user_id}`}>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(u)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`super-admin-delete-confirm-${u.user_id}`}>
                              Delete Super Admin
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-super-admin-dialog">
          <DialogHeader><DialogTitle>Edit {editing?.name}</DialogTitle></DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="space-y-2"><Label>Name</Label>
              <Input required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-super-admin-name" className="rounded-sm" /></div>
            <div className="space-y-2"><Label>Email</Label>
              <Input type="email" required value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-super-admin-email" className="rounded-sm" /></div>
            <div className="space-y-2"><Label>Reset Password <span className="text-muted-foreground text-xs">(leave blank to keep)</span></Label>
              <PasswordInput value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="New password" data-testid="edit-super-admin-password" className="rounded-sm" />
              {editForm.password && <PasswordStrength value={editForm.password} />}</div>
            <DialogFooter>
              <Button type="submit" disabled={busy} className="rounded-sm" data-testid="edit-super-admin-submit">
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
